import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EntityRESTService } from '../../../services/entity-rest.service';
import { DrupalRESTService } from '../../../services/drupal-rest.service';
import { TaxonomyService } from '../../../services/taxonomy.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { PrintService } from '../../../services/print.service';
import { ReportDateService } from '../../../services/report-date.service';
import { CurrencyService } from '../../../services/currency.service';
import { TranslateService } from '@ngx-translate/core';
import { StudioConfigService } from '../../../services/studio-config.service';
import { LoggerService } from '../../../services/logger.service';

// Define interfaces for the object types
interface EntityReference {
  target_id: string | number;
  target_type?: string;
  target_uuid?: string;
  entity?: any;
  name?: string;
  field_enrollment_id?: string;
  field_enrollment_package_name?: any; // Generic any type to avoid TypeScript errors
  field_enrollment_package_label?: any; // Snapshot label pinned on the enrollment at sale time
  title?: string;
}

interface PackageReference {
  id?: string | number;
  title?: string;
  field_package_name?: string;
  [key: string]: any;
}

interface PackageData {
  id?: string | number;
  name?: string;
  field_enrollment_id?: string;
  field_package_name?: string;
  title?: string;
  [key: string]: any;
}

interface StudentData {
  id?: string | number;
  name?: string;
  [key: string]: any;
}

interface PaymentTypeData {
  id?: string | number;
  name?: string;
  [key: string]: any;
}

interface PaymentEntity {
  id?: string | number;
  name?: string;
  field_payment_id?: string;
  field_enrollment_name?: EntityReference;
  field_student_name?: EntityReference;
  field_payment_type?: EntityReference;
  field_customer_name?: string;
  field_date_and_time?: string;
  field_gross_tuition?: string;
  field_tax_collected?: string;
  field_total_payment_amount?: string;
  field_reference_number?: string;
  field_surcharge?: string;
  field_cancellation_fee?: string;
  field_card_last4?: string;
  /** 'ach' for a PayBright bank debit (set by the payment form on success). */
  field_payment_method?: string;
  field_paybright_txn_object?: string;
  field_paybright_reference_number?: string;
  field_scheduled_payment?: any;
  field_status?: string;
  field_customer_email?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-payment-receipt',
  templateUrl: './payment-receipt.component.html',
  styleUrls: ['./payment-receipt.component.css']
})
export class PaymentReceiptComponent implements OnInit, OnDestroy {
  @Input() paymentData: PaymentEntity = {};
  @Input() inlineMode = false;

  studioInfo: any = {};
  packageData: PackageData = {};
  studentData: StudentData = {};
  paymentTypeData: PaymentTypeData = {};
  loading = true;
  today = new Date();

  // Fiscal Code (Codice Fiscale) is shown when the studio or the student is in
  // Italy/Spain. Studio side is resolved up-front; student side is read from the
  // loaded student account (see shouldShowFiscalCode()).
  private studioIsFiscalCountry = false;

  constructor(
    private entityRESTService: EntityRESTService,
    private drupalRESTService: DrupalRESTService,
    private _printService: PrintService,
    private taxonomyService: TaxonomyService,
    public reportDateService: ReportDateService,
    private currencyService: CurrencyService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private studioConfigService: StudioConfigService,
    @Optional() public dialogRef: MatDialogRef<PaymentReceiptComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { paymentId: number }
  ) { }

  // Studio config is app-lifetime; released so each closed receipt can be collected.
  private countryCodeSubscription: Subscription;

  ngOnDestroy(): void {
    this.countryCodeSubscription?.unsubscribe();
  }

  ngOnInit() {
    this.loadStudioInformation();

    // Resolve the studio side of the Fiscal Code gate (student side is read
    // per-render from the loaded student account).
    this.countryCodeSubscription = this.studioConfigService.getCountryCode().subscribe(countryCode => {
      this.studioIsFiscalCountry = ['IT', 'ES'].includes(countryCode);
    });

    // Diagnostic log
    LoggerService.log('[DEBUG] TaxonomyService payment_type array:', this.taxonomyService.payment_type);
    LoggerService.log('[DEBUG] Component initialized, checking for taxonomy data');

    // If we have direct payment data (inline mode)
    if (this.paymentData && Object.keys(this.paymentData).length > 0) {
      LoggerService.log('Payment Receipt initialized with direct data:', this.paymentData);

      // Log taxonomy data availability
      LoggerService.log('Available payment types in taxonomy:', this.taxonomyService.payment_type.length);
      LoggerService.log('Available package codes in taxonomy:', this.taxonomyService.package_code.length);

      // If we have payment type info, check if it's available in taxonomy
      if (this.paymentData.field_payment_type?.target_id) {
        const paymentTypeId = this.paymentData.field_payment_type.target_id;
        const paymentTypeFromTaxonomy = this.taxonomyService.payment_type.find(
          (item: any) => item.id == paymentTypeId
        );
        if (paymentTypeFromTaxonomy) {
          LoggerService.log('Found payment type in taxonomy:', paymentTypeFromTaxonomy.name);
          this.paymentTypeData = paymentTypeFromTaxonomy;
        } else {
          LoggerService.log('Payment type not found in taxonomy, will fetch from API');
        }
      }

      // If we already have a field_enrollment_package_name, try to pre-populate packageData
      if (this.paymentData.field_enrollment_name?.field_enrollment_package_name) {
        const packageId = this.paymentData.field_enrollment_name.field_enrollment_package_name;
        LoggerService.log('Pre-populating package data with ID:', packageId);

        // Try to find in taxonomy first if numeric
        if (typeof packageId === 'string' && !isNaN(Number(packageId))) {
          const packageFromTaxonomy = this.taxonomyService.package_code.find(
            (item: any) => item.id == packageId
          );
          if (packageFromTaxonomy) {
            LoggerService.log('Found package in taxonomy:', packageFromTaxonomy.name);
            this.packageData = { name: packageFromTaxonomy.name } as PackageData;
          } else {
            LoggerService.log('Package not found in taxonomy, will fetch from API');
            this.fetchPackageNameFromTaxonomy(packageId);
          }
        } else {
          // If it's already a descriptive name, use it directly
          this.packageData.name = packageId as string;
        }
      }

      // Set loading to false after a small delay to ensure studio info is loaded
      setTimeout(() => {
        this.loading = false;
      }, 500);

      // Load related data for this payment
      this.loadRelatedData();
    }
    // If we have a payment ID from dialog data
    else if (this.data?.paymentId) {
      LoggerService.log('Payment Receipt initialized with paymentId:', this.data.paymentId);
      this.fetchPaymentData();
    } else {
      // No data provided
      LoggerService.error('No payment data or ID provided to the receipt component');
      this.loading = false;
    }
  }

  loadStudioInformation() {
    const configFieldName = 'field_studio_information_ref';
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe({
        next: (response) => {
          LoggerService.log('Studio information response:', response);
          this.studioInfo = {
            name: response?.['field_studio_display_name']?.[0]?.value || 'Business Name',
            address: response?.['field_studio_address']?.[0]?.address_line1 || 'Street Address',
            city: response?.['field_studio_address']?.[0]?.locality || 'City',
            state: response?.['field_studio_address']?.[0]?.administrative_area || 'State',
            zipCode: response?.['field_studio_address']?.[0]?.postal_code || 'Zip Code',
            phone: response?.['field_studio_phone_number']?.[0]?.value || 'Phone Number',
            website: response?.['field_studio_website']?.[0]?.uri || 'Website',
            email: response?.['field_studio_email']?.[0]?.value || 'Email'
          };
        },
        error: (error) => {
          LoggerService.error('Error loading studio information:', error);
        }
      });
  }

  fetchPaymentData() {
    this.loading = true;
    LoggerService.log('Fetching payment data with ID:', this.data.paymentId);

    // First try to get a fully expanded payment entity
    this.drupalRESTService.httpGET(`/api_entity/eck/payments/payment/${this.data.paymentId}?_format=json&_expand=1`)
      .pipe(
        catchError(error => {
          LoggerService.log('Expanded entity API failed, falling back to regular entity API:', error);
          return this.drupalRESTService.httpGET(`/api_rest/v1/payments/${this.data.paymentId}`);
        }),
        tap((payment: PaymentEntity) => {
          LoggerService.log('Payment data received:', payment);
          this.paymentData = payment;
          // The fallback API may omit the entity id; the email button needs it.
          if (!this.paymentData.id) {
            this.paymentData.id = this.data.paymentId;
          }

          // Check if the payment data already has the expanded references
          if (payment?.field_enrollment_name?.entity) {
            this.packageData = payment.field_enrollment_name.entity as PackageData;
            LoggerService.log('Package data found in entity:', this.packageData);
          }

          if (payment?.field_student_name?.entity) {
            this.studentData = payment.field_student_name.entity as StudentData;
            LoggerService.log('Student data found in entity:', this.studentData);
          }

          if (payment?.field_payment_type?.entity) {
            this.paymentTypeData = payment.field_payment_type.entity as PaymentTypeData;
            LoggerService.log('Payment type data found in entity:', this.paymentTypeData);
          }

          // Debug output
          LoggerService.log('Payment Type:', this.getPaymentTypeName());
          LoggerService.log('Package Name:', this.getPackageName());
          LoggerService.log('Student Name:', this.getStudentName());
          LoggerService.log('Enrollment ID:', this.getEnrollmentId());
        }),
        switchMap((payment: PaymentEntity) => {
          // Prepare observables for related entities
          const requests = [];

          // Get package/enrollment details if available and not already expanded
          if (payment.field_enrollment_name && payment.field_enrollment_name.target_id && !this.packageData?.name) {
            LoggerService.log('Fetching enrollment data with ID:', payment.field_enrollment_name.target_id);
            // Try both endpoints to maximize chances of getting package data
            requests.push(
              this.drupalRESTService.httpGET(`/api_rest/v1/packages/get/${payment.field_enrollment_name.target_id}`)
                .pipe(
                  tap(data => {
                    LoggerService.log('Enrollment data received from packages/get:', data);
                    if (data) this.packageData = data;
                  }),
                  catchError(error => {
                    LoggerService.error('Error fetching enrollment from packages/get:', error);
                    return this.entityRESTService.getEntity('packages', 'enrollment', payment.field_enrollment_name.target_id)
                      .pipe(
                        tap(data => LoggerService.log('Enrollment data received from entityRESTService:', data)),
                        catchError(innerError => {
                          LoggerService.error('Error fetching enrollment from entityRESTService:', innerError);
                          return of(null);
                        })
                      );
                  })
                )
            );
          } else {
            requests.push(of(null));
          }

          // Get student details if available and not already expanded
          if (payment.field_student_name && payment.field_student_name.target_id && !this.studentData?.name) {
            requests.push(
              this.entityRESTService.getEntity('student_accounts', 'student_account', payment.field_student_name.target_id)
                .pipe(
                  tap(data => LoggerService.log('Student data received:', data)),
                  catchError(error => {
                    LoggerService.error('Error fetching student account data:', error);
                    return of(null);
                  })
                )
            );
          } else {
            requests.push(of(null));
          }

          // Get payment type details if available and not already expanded
          if (payment.field_payment_type && payment.field_payment_type.target_id && !this.paymentTypeData?.name) {
            LoggerService.log('Fetching payment type data with ID:', payment.field_payment_type.target_id);
            requests.push(
              this.drupalRESTService.httpGET(`/api_rest/v1/taxonomy_term/${payment.field_payment_type.target_id}`)
                .pipe(
                  tap(data => LoggerService.log('Payment type data received:', data)),
                  catchError(error => {
                    LoggerService.error('Error fetching payment type data:', error);
                    // Try an alternative endpoint for taxonomy terms
                    return this.drupalRESTService.httpGET(`/api_rest/v1/taxonomy_term/name/${payment.field_payment_type.target_id}`)
                      .pipe(
                        tap(name => {
                          if (name && typeof name === 'string') {
                            LoggerService.log('Payment type name received:', name);
                            this.paymentTypeData = { name: name } as PaymentTypeData;
                          }
                        }),
                        catchError(innerError => {
                          LoggerService.error('Error fetching payment type name:', innerError);
                          return of(null);
                        })
                      );
                  })
                )
            );
          } else {
            requests.push(of(null));
          }

          // If all entities are already expanded, return empty array
          if (requests.every(req => req === of(null))) {
            LoggerService.log('All entity data already available in payment response');
          }

          return forkJoin(requests);
        }),
        catchError(error => {
          LoggerService.error('Error fetching payment data:', error);
          this.loading = false;
          return of([null, null, null]);
        })
      )
      .subscribe({
        next: ([enrollmentData, studentData, paymentTypeData]) => {
          // Only update if we got new data and don't already have it
          if (enrollmentData && !this.packageData?.name) {
            LoggerService.log('Setting package data from API response');
            this.packageData = enrollmentData as PackageData;
            LoggerService.log('Updated package data:', this.packageData);
          }

          if (studentData && !this.studentData?.name) {
            LoggerService.log('Setting student data from API response');
            this.studentData = studentData as StudentData;
            LoggerService.log('Updated student data:', this.studentData);
          }

          if (paymentTypeData && !this.paymentTypeData?.name) {
            LoggerService.log('Setting payment type data from API response');
            this.paymentTypeData = paymentTypeData as PaymentTypeData;
            LoggerService.log('Updated payment type data:', this.paymentTypeData);
          }

          // If we have a target_id but no entity data, try one more direct approach
          this.ensureReferenceData();

          this.loading = false;

          // Debug output again after all fetches
          LoggerService.log('Final Payment Type:', this.getPaymentTypeName());
          LoggerService.log('Final Package Name:', this.getPackageName());
          LoggerService.log('Final Student Name:', this.getStudentName());
          LoggerService.log('Final Enrollment ID:', this.getEnrollmentId());
        }
      });
  }

  ensureReferenceData() {
    // Direct lookup for payment type through taxonomy service first
    if (this.paymentData?.field_payment_type?.target_id && !this.getPaymentTypeName()) {
      const paymentTypeId = this.paymentData.field_payment_type.target_id;
      LoggerService.log('Checking payment type in taxonomy service:', paymentTypeId);

      // Check if it's in the taxonomy service
      const paymentTypeFromTaxonomy = this.taxonomyService.payment_type.find(
        (item: any) => item.id == paymentTypeId
      );

      if (paymentTypeFromTaxonomy) {
        LoggerService.log('Found payment type in taxonomy service:', paymentTypeFromTaxonomy.name);
        this.paymentTypeData = paymentTypeFromTaxonomy;
      } else {
        LoggerService.log('Fallback: directly getting payment type from taxonomy term API');
        // Get the payment type from payment_type taxonomy vocabulary
        this.drupalRESTService.httpGET(`/api_rest/v1/taxonomy/payment_type`)
          .subscribe({
            next: (taxonomyTerms: any[]) => {
              LoggerService.log('All payment types:', taxonomyTerms);
              if (Array.isArray(taxonomyTerms)) {
                const paymentType = taxonomyTerms.find(
                  term => term.id == this.paymentData.field_payment_type.target_id
                );
                if (paymentType) {
                  LoggerService.log('Found payment type:', paymentType);
                  this.paymentTypeData = paymentType;
                }
              }
            },
            error: (error) => {
              LoggerService.error('Error fetching payment types taxonomy:', error);
            }
          });
      }
    }

    // Check for package in taxonomy service first if we have field_enrollment_package_name
    const currentPackageName = this.getPackageName();
    const needsPackageData = !currentPackageName || currentPackageName === 'N/A';

    if (this.paymentData?.field_enrollment_name?.field_enrollment_package_name && needsPackageData) {
      const packageId = this.paymentData.field_enrollment_name.field_enrollment_package_name;

      if (typeof packageId === 'string' && !isNaN(Number(packageId))) {
        // Check if it's in the taxonomy service
        const packageFromTaxonomy = this.taxonomyService.package_code.find(
          (item: any) => item.id == packageId
        );

        if (packageFromTaxonomy) {
          this.packageData = { name: packageFromTaxonomy.name } as PackageData;
        } else {
          // If not in taxonomy service, try loading the package entity directly
          this.loadPackageById(packageId);
        }
      }
    }

    // Direct lookup for package/enrollment
    if (this.paymentData?.field_enrollment_name?.target_id && needsPackageData) {
      LoggerService.log('Fallback: directly getting enrollment data');

      // Try direct API for enrollment by ID
      this.drupalRESTService.httpGET(`/api_rest/v1/enrollments/${this.paymentData.field_enrollment_name.target_id}`)
        .subscribe({
          next: (response: any) => {
            LoggerService.log('Direct enrollment API response:', response);
            if (response) {
              this.packageData = response;
            }
          },
          error: (error) => {
            LoggerService.error('Error fetching enrollment by ID:', error);

            // Try package name endpoint
            this.drupalRESTService.httpGET(`/api_rest/v1/packages/name/${this.paymentData.field_enrollment_name.target_id}`)
              .subscribe({
                next: (response: any) => {
                  LoggerService.log('Package name response:', response);
                  if (response && typeof response === 'string') {
                    this.packageData = { name: response } as PackageData;
                  }
                },
                error: (error) => {
                  LoggerService.error('Error fetching package name:', error);
                }
              });
          }
        });
    }
  }

  formatCardNumber(cardNumber: string | number): string {
    if (cardNumber === null || cardNumber === undefined || cardNumber === '') return 'N/A';

    // Assuming only last 4 digits are stored or visible
    return `xxxx-xxxx-xxxx-${String(cardNumber).slice(-4)}`;
  }

  /** Masked bank account for an ACH receipt line. */
  formatBankAccount(last4: string | number): string {
    return `Bank account ending in ${String(last4).slice(-4)}`;
  }

  /**
   * The masked card number to print, or '' when unknown.
   *
   * Prefers the real card last-4 (passed straight from a gateway response, or
   * read back from the stored PayBright transaction object on a re-print). A
   * gateway payment's field_reference_number is a transaction id, not a card,
   * so it is only used as a legacy fallback for non-gateway payments.
   */
  getCardNumberDisplay(): string {
    if (this.paymentData.field_card_last4) {
      return this.paymentData.field_payment_method === 'ach'
        ? this.formatBankAccount(this.paymentData.field_card_last4)
        : this.formatCardNumber(this.paymentData.field_card_last4);
    }
    const txn = this.paymentData.field_paybright_txn_object;
    if (txn) {
      try {
        const parsed = typeof txn === 'string' ? JSON.parse(txn) : txn;
        // An ACH debit carries check_details instead of card_details.
        const check = parsed?.transaction?.check_details ?? parsed?.check_details;
        if (check) {
          const bankLast4 = parsed?.last_4 ?? check.account_number_last4 ?? check.last4;
          return bankLast4 ? this.formatBankAccount(bankLast4) : 'Bank account';
        }
        const last4 = parsed?.last_4 ?? parsed?.transaction?.card_details?.last4;
        if (last4) {
          return this.formatCardNumber(last4);
        }
      } catch (e) {
        // Fall through.
      }
      return '';
    }
    if (this.paymentData.field_paybright_reference_number) {
      return '';
    }
    return this.paymentData.field_reference_number ? this.formatCardNumber(this.paymentData.field_reference_number) : '';
  }

  /** Gateway card surcharge as a number (0 when none). */
  getSurcharge(): number {
    const v = Number(this.paymentData.field_surcharge);
    return isFinite(v) && Math.abs(v) > 0.001 ? v : 0;
  }

  /** Total + surcharge: what actually hit the card. */
  getTotalCharged(): number {
    const total = Number(this.paymentData.field_total_payment_amount || this.paymentData.field_gross_tuition || 0);
    return total + this.getSurcharge();
  }

  /** Cancellation fee withheld from a refund (0 when none). */
  getCancellationFee(): number {
    const v = Number(this.paymentData.field_cancellation_fee);
    return isFinite(v) && Math.abs(v) > 0.001 ? v : 0;
  }

  /** The refund as calculated before the fee was withheld (signed). */
  getRefundBeforeFee(): number {
    const total = Number(this.paymentData.field_total_payment_amount || this.paymentData.field_gross_tuition || 0);
    return total - this.getCancellationFee();
  }

  /** Whether this receipt records money returned (negative amount). */
  isRefundReceipt(): boolean {
    const total = Number(this.paymentData.field_total_payment_amount || this.paymentData.field_gross_tuition || 0);
    return total < -0.001;
  }

  /**
   * Whether this receipt is a refund that had a fee withheld, as opposed to a
   * fee-only charge ($0 tuition); only the former shows a before-fee line.
   */
  isRefundWithFee(): boolean {
    const total = Number(this.paymentData.field_total_payment_amount || this.paymentData.field_gross_tuition || 0);
    return this.getCancellationFee() > 0 && total < -0.001;
  }

  getFormattedDate(date: string): string {
    return this.reportDateService.formatDate(date, 'N/A');
  }

  getPackageName(): string {
    // Get the enrollment package reference data
    const enrollmentName = this.paymentData?.field_enrollment_name;
    const packageRef = enrollmentName?.field_enrollment_package_name;

    // Case 0: the label snapshot pinned on the enrollment at sale time
    // ("{id} - {name}", ami_enrollment_history). Shown without the id prefix
    // so it reads like the raw package name the other cases return.
    const snapshotLabel = enrollmentName?.field_enrollment_package_label;
    const snapshotValue = typeof snapshotLabel === 'string' ? snapshotLabel : snapshotLabel?.[0]?.value;
    if (snapshotValue) {
      return String(snapshotValue).replace(/^\d+\s*-\s*/, '');
    }

    // Case 1: Package data is fully resolved with field_package_name property
    if (packageRef && typeof packageRef === 'object' && packageRef.field_package_name) {
      return packageRef.field_package_name;
    }

    // Case 2: Package has title but no field_package_name
    if (packageRef && typeof packageRef === 'object' && packageRef.title) {
      return packageRef.title;
    }

    // // Case 3: Enrollment itself has a title
    // if (enrollmentName && enrollmentName.title) {
    //   return enrollmentName.title;
    // }

    // Case 4: Package data is in our packageData object
    if (this.packageData?.name) {
      return this.packageData.name;
    }

    // Case 5: Package data title is in our packageData object
    // if (this.packageData?.['title']) {
    //   return this.packageData['title'];
    // }

    // Case 6: Package has field_package_name in packageData
    if (this.packageData?.['field_package_name']) {
      return this.packageData['field_package_name'];
    }

    // Case 7: Package name is in the enrollment name property
    if (enrollmentName?.name) {
      return enrollmentName.name;
    }

    // Case 8: Package name is in the enrollment entity
    if (enrollmentName?.entity?.name) {
      return enrollmentName.entity.name;
    }

    // Case 9: Package title is in the enrollment entity
    if (enrollmentName?.entity?.['title']) {
      return enrollmentName.entity['title'];
    }

    // Case 10: Package field_package_name is in the enrollment entity
    if (enrollmentName?.entity?.['field_package_name']) {
      return enrollmentName.entity['field_package_name'];
    }

    // Case 11: Payment data has a direct package_name property
    if (this.paymentData?.['package_name']) {
      return this.paymentData['package_name'];
    }

    // Case 12: Payment data has a direct enrollment_name property
    if (this.paymentData?.['enrollment_name']) {
      return this.paymentData['enrollment_name'];
    }

    // Case 13: packageRef is a string ID, so return loading state but don't trigger fetches from here
    if (packageRef && typeof packageRef === 'string' && !isNaN(Number(packageRef))) {
      return this.isPackageDataLoading ? 'Loading...' : 'N/A';
    }

    // Case 14: packageRef is a non-numeric string
    if (packageRef && typeof packageRef === 'string') {
      return packageRef;
    }

    // Case 15: packageRef is an object but doesn't have the expected properties
    if (packageRef && typeof packageRef === 'object') {
      return JSON.stringify(packageRef);
    }

    // Default: No package name found
    return 'N/A';
  }

  getStudentName(): string {
    // Try different sources for the student name
    if (this.studentData?.name) {
      return this.studentData.name;
    } else if (this.studentData?.['title']) {
      return this.studentData['title'];
    } else if (this.paymentData?.field_student_name?.name) {
      return this.paymentData.field_student_name.name;
    } else if (this.paymentData?.field_student_name?.entity?.name) {
      return this.paymentData.field_student_name.entity.name;
    } else if (this.paymentData?.field_student_name?.entity?.['title']) {
      return this.paymentData.field_student_name.entity['title'];
    } else if (this.paymentData?.field_customer_name) {
      return this.paymentData.field_customer_name;
    } else if (this.paymentData?.['student_name']) {
      return this.paymentData['student_name'];
    } else {
      return 'N/A';
    }
  }

  // The student's primary contact (field_contacts[0]) holds both the fiscal code
  // and the address used to decide whether to show it.
  private getPrimaryContact(): any {
    const contacts = this.studentData?.['field_contacts']
      ?? this.paymentData?.field_student_name?.entity?.['field_contacts'];
    return Array.isArray(contacts) ? contacts[0] : contacts;
  }

  getFiscalCode(): string {
    return this.getPrimaryContact()?.field_fiscal_code || '';
  }

  // Show the Fiscal Code row when the studio OR the student is in Italy/Spain.
  shouldShowFiscalCode(): boolean {
    if (this.studioIsFiscalCountry) { return true; }
    const studentCountry = this.getPrimaryContact()?.field_address?.country_code;
    return ['IT', 'ES'].includes(studentCountry);
  }

  getPaymentTypeName(): string {
    // Case 1: Direct payment type ID as string
    if (this.paymentData?.field_payment_type && typeof this.paymentData.field_payment_type === 'string') {
      // Look up in taxonomy service
      const paymentType = this.taxonomyService.payment_type.find(
        (item: any) => item.id === this.paymentData.field_payment_type
      );
      if (paymentType) {
        return paymentType.name;
      }
    }

    // Case 2: Payment type in paymentTypeData
    if (this.paymentTypeData?.name) {
      return this.paymentTypeData.name;
    }

    // Case 3: Payment type name in field_payment_type
    if (this.paymentData?.field_payment_type?.name) {
      return this.paymentData.field_payment_type.name;
    }

    // Case 4: Payment type name in field_payment_type entity
    if (this.paymentData?.field_payment_type?.entity?.name) {
      return this.paymentData.field_payment_type.entity.name;
    }

    // Case 5: Direct payment_type_name property
    if (this.paymentData?.['payment_type_name']) {
      return this.paymentData['payment_type_name'];
    }

    // Case 6: Payment type target_id lookup in taxonomy
    if (this.paymentData?.field_payment_type?.target_id) {
      const paymentTypeId = this.paymentData.field_payment_type.target_id;
      const paymentType = this.taxonomyService.payment_type.find(
        (item: any) => item.id == paymentTypeId
      );
      if (paymentType) {
        return paymentType.name;
      }
    }

    // Default: No payment type found
    return 'N/A';
  }

  getEnrollmentId(): string {
    if (this.packageData?.field_enrollment_id) {
      return this.packageData.field_enrollment_id;
    } else if (this.paymentData?.field_enrollment_name?.field_enrollment_id) {
      return this.paymentData.field_enrollment_name.field_enrollment_id;
    } else if (this.paymentData?.field_enrollment_name?.entity?.field_enrollment_id) {
      return this.paymentData.field_enrollment_name.entity.field_enrollment_id;
    } else {
      return 'N/A';
    }
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  printReceipt(): void {
    // Get the studio info and receipt data
    const studioName = this.studioInfo.name || 'Business Name';
    const studioAddress = this.studioInfo.address || 'Street Address';
    const studioCity = this.studioInfo.city || '';
    const studioState = this.studioInfo.state || '';
    const studioZipCode = this.studioInfo.zipCode || '';
    const studioPhone = this.studioInfo.phone || 'Phone Number';

    // Format the receipt data
    const packageName = this.getPackageName();
    const paymentId = this.paymentData.field_payment_id || 'N/A';
    const date = this.getFormattedDate(this.paymentData.field_date_and_time);
    const paymentMethod = this.getPaymentTypeName();
    const studentName = this.getStudentName();
    const amountPaid = this.paymentData.field_gross_tuition || '0.00';
    const taxAmount = this.paymentData.field_tax_collected || '0.00';
    const totalAmount = this.paymentData.field_total_payment_amount || this.paymentData.field_gross_tuition || '0.00';
    const enrollmentId = this.getEnrollmentId();
    const cardNumber = this.getCardNumberDisplay();
    const surcharge = this.getSurcharge();
    const totalCharged = this.getTotalCharged();
    const cancellationFee = this.getCancellationFee();
    const refundBeforeFee = this.getRefundBeforeFee();
    const customerName = this.paymentData.field_customer_name || 'Cardholder Name';
    const fiscalCode = this.shouldShowFiscalCode() ? this.getFiscalCode() : '';

    // Resolve translated labels for the print template. The print output is
    // written into a separate browser window, so the | translate pipe can't
    // reach it. Use TranslateService.instant() here instead.
    const t = (key: string, params?: object) => this.translate.instant('PAYMENTS.RECEIPT.' + key, params);
    const L = {
      date: t('DATE'),
      receiptNumber: t('RECEIPT_NUMBER'),
      paymentMethod: t('PAYMENT_METHOD'),
      cardNumber: t('CARD_NUMBER'),
      student: t('STUDENT'),
      fiscalCode: t('FISCAL_CODE'),
      packageLabel: t('PACKAGE'),
      amount: this.isRefundReceipt() ? t('AMOUNT_REFUNDED') : t('AMOUNT'),
      tax: t('TAX'),
      total: t('TOTAL'),
      surcharge: t('SURCHARGE'),
      totalCharged: t('TOTAL_CHARGED'),
      cancellationFee: t('CANCELLATION_FEE'),
      refundBeforeFee: t('REFUND_BEFORE_FEE'),
      signature: t('SIGNATURE'),
      agreementPay: t('AGREEMENT_PAY'),
      agreementAuthorize: t('AGREEMENT_AUTHORIZE', { studioName, enrollmentId }),
      agreementCancellation: t('AGREEMENT_CANCELLATION'),
      customerCopy: t('CUSTOMER_COPY'),
      retainRecords: t('RETAIN_RECORDS'),
      receiptGenerated: t('RECEIPT_GENERATED'),
    };

    // Build the receipt content
    const content = `
      <div class="receipt">
        <div class="receipt-header">
          <div class="receipt-business-name">${studioName}</div>
          <div class="receipt-business-address">${studioAddress}</div>
          <div class="receipt-business-location">${studioCity}, ${studioState} ${studioZipCode}</div>
          <div class="receipt-business-phone">${studioPhone}</div>
          <div class="receipt-divider">================================</div>
        </div>

        <div class="receipt-details">
          <div class="receipt-row">
            <div class="receipt-label">${L.date}</div>
            <div class="receipt-value">${date}</div>
          </div>
          <div class="receipt-row">
            <div class="receipt-label">${L.receiptNumber}</div>
            <div class="receipt-value">${paymentId}</div>
          </div>
          <div class="receipt-row">
            <div class="receipt-label">${L.paymentMethod}</div>
            <div class="receipt-value">${paymentMethod}</div>
          </div>
          ${cardNumber ? `
          <div class="receipt-row">
            <div class="receipt-label">${L.cardNumber}</div>
            <div class="receipt-value">${cardNumber}</div>
          </div>
          ` : ''}
          <div class="receipt-row">
            <div class="receipt-label">${L.student}</div>
            <div class="receipt-value">${studentName}</div>
          </div>
          ${fiscalCode ? `
          <div class="receipt-row">
            <div class="receipt-label">${L.fiscalCode}</div>
            <div class="receipt-value">${fiscalCode}</div>
          </div>
          ` : ''}
          <div class="receipt-row">
            <div class="receipt-label">${L.packageLabel}</div>
            <div class="receipt-value">${packageName}</div>
          </div>
          <div class="receipt-divider">--------------------------------</div>
        </div>

        <div class="receipt-amount">
          <div class="receipt-row">
            <div class="receipt-label">${L.amount}</div>
            <div class="receipt-value">${this.currencyService.format(amountPaid)}</div>
          </div>
          ${Number(taxAmount) > 0 ? `
          <div class="receipt-row">
            <div class="receipt-label">${L.tax}</div>
            <div class="receipt-value">${this.currencyService.format(taxAmount)}</div>
          </div>
          ` : ''}
          <div class="receipt-row total">
            <div class="receipt-label">${L.total}</div>
            <div class="receipt-value">${this.currencyService.format(totalAmount)}</div>
          </div>
          ${surcharge ? `
          <div class="receipt-row">
            <div class="receipt-label">${L.surcharge}</div>
            <div class="receipt-value">${this.currencyService.format(surcharge)}</div>
          </div>
          <div class="receipt-row total">
            <div class="receipt-label">${L.totalCharged}</div>
            <div class="receipt-value">${this.currencyService.format(totalCharged)}</div>
          </div>
          ` : ''}
          ${cancellationFee ? `
          ${Number(totalAmount) < -0.001 ? `
          <div class="receipt-row">
            <div class="receipt-label">${L.refundBeforeFee}</div>
            <div class="receipt-value">${this.currencyService.format(refundBeforeFee)}</div>
          </div>
          ` : ''}
          <div class="receipt-row">
            <div class="receipt-label">${L.cancellationFee}</div>
            <div class="receipt-value">${this.currencyService.format(cancellationFee)}</div>
          </div>
          ` : ''}
          <div class="receipt-divider">================================</div>
        </div>

        <div class="receipt-signature">
          <div class="receipt-sig-line">____________________________</div>
          <div class="receipt-sig-label">${L.signature}</div>
        </div>

        <div class="receipt-agreement">
          <div class="receipt-agreement-text">
            ${L.agreementPay}
            ${enrollmentId !== 'N/A' ? L.agreementAuthorize : ''}
            ${L.agreementCancellation}
          </div>
        </div>

        <div class="receipt-footer">
          <div class="receipt-footer-text">${L.customerCopy}</div>
          <div class="receipt-footer-text">${L.retainRecords}</div>
          <div class="receipt-footer-date">${L.receiptGenerated} ${this.reportDateService.formatDate(new Date())} ${this.reportDateService.formatTime(new Date())}</div>
        </div>
      </div>
    `;

    // Define receipt-specific styles
    const styles = `
      @page {
        size: 80mm 200mm;
        margin: 0mm;
      }

      .receipt {
        font-family: "Courier New", Courier, monospace;
        font-size: 12px;
        width: 80mm;
        padding: 5mm;
        margin: 0 auto;
        text-align: left;
      }

      .receipt-header {
        text-align: center;
        margin-bottom: 10px;
      }

      .receipt-business-name {
        font-size: 14px;
        font-weight: bold;
      }

      .receipt-divider {
        font-family: monospace;
        margin: 10px 0;
        white-space: pre;
      }

      .receipt-row {
        display: flex;
        justify-content: space-between;
        margin: 5px 0;
      }

      .receipt-label {
        flex: 1;
        font-weight: normal;
      }

      .receipt-value {
        flex: 1;
        text-align: right;
      }

      .receipt-amount {
        margin: 10px 0;
      }

      .total {
        font-weight: bold;
      }

      .receipt-signature {
        margin: 20px 0;
        text-align: center;
      }

      .receipt-sig-line {
        margin-bottom: 5px;
      }

      .receipt-sig-label {
        font-style: italic;
      }

      .receipt-agreement {
        font-size: 10px;
        margin: 15px 0;
      }

      .receipt-footer {
        margin-top: 20px;
        text-align: center;
        font-size: 10px;
      }

      .receipt-footer-text {
        margin: 2px 0;
      }

      .receipt-footer-date {
        margin-top: 10px;
        font-style: italic;
      }

      @media print {
        body {
          margin: 0;
          padding: 0;
          width: 80mm;
        }

        .no-print {
          display: none !important;
        }
      }
    `;

    // Use the print service
    this._printService.print(content, {
      title: `${this.translate.instant('PAYMENTS.RECEIPT.TITLE')} #${paymentId}`,
      styles: styles,
      promptPrint: true,
      showSchoolName: false
    });
  }

  emailReceipt(): void {
    LoggerService.log('Sending receipt via email');

    // The endpoint loads the payment by entity id. field_payment_id is the
    // receipt number, which is not unique, so it must never be sent in its
    // place: the server would either fail or find someone else's payment.
    const paymentID = this.paymentData.id;
    if (!paymentID) {
      LoggerService.error('No payment entity ID available');
      this.snackBar.open(this.translate.instant('PAYMENTS.RECEIPT.EMAIL_NO_ID'), this.translate.instant('COMMON.CLOSE'), {
        duration: 3000,
      });
      return;
    }

    // Call the API to send the email
    this.drupalRESTService.httpGET(`/api_rest/v1/sendPaymentReceiptEmail?paymentID=${paymentID}`)
      .subscribe({
        next: (response) => {
          if (response?.['status'] === 'error') {
            LoggerService.error('Error sending email:', response);
            this.snackBar.open(this.translate.instant('PAYMENTS.RECEIPT.EMAIL_ERROR'), this.translate.instant('COMMON.CLOSE'), {
              duration: 3000,
            });
            return;
          }
          LoggerService.log('Email sent successfully:', response);
          // Show success message
          this.snackBar.open(this.translate.instant('PAYMENTS.RECEIPT.EMAIL_SUCCESS'), this.translate.instant('COMMON.CLOSE'), {
            duration: 3000,
          });
        },
        error: (error) => {
          LoggerService.error('Error sending email:', error);
          // Show error message
          this.snackBar.open(this.translate.instant('PAYMENTS.RECEIPT.EMAIL_ERROR'), this.translate.instant('COMMON.CLOSE'), {
            duration: 3000,
          });
        }
      });
  }

  // Load related student, package, and payment type data
  loadRelatedData() {
    LoggerService.log('Loading related data for receipt...');

    // Load student data if needed and not already loaded
    if (this.paymentData.field_student_name?.target_id && !this.studentData?.name) {
      this.fetchStudentData(this.paymentData.field_student_name.target_id);
    } else {
      LoggerService.log('Student data already loaded or no student ID found');
    }

    // Load package data if needed and not already loaded
    if (!this.packageData?.name && !this.packageData?.field_package_name) {
      if (this.paymentData.field_enrollment_name?.target_id) {
        this.fetchPackageData(this.paymentData.field_enrollment_name.target_id);
      } else if (this.paymentData.field_enrollment_name?.field_enrollment_package_name) {
        // If we have a package name reference but no target_id, try to get that package name
        this.fetchPackageNameFromTaxonomy(this.paymentData.field_enrollment_name.field_enrollment_package_name);
        LoggerService.log('Fetching package name from taxonomy:', this.paymentData.field_enrollment_name.field_enrollment_package_name);
      } else {
        LoggerService.log('No enrollment ID found in payment data');
      }
    } else {
      LoggerService.log('Package data already loaded');
    }
  }

  // Add a new method to fetch package name from taxonomy term
  fetchPackageNameFromTaxonomy(packageTermId: string | number) {
    if (!packageTermId || packageTermId === 'N/A') {
      LoggerService.log('Invalid package term ID');
      return;
    }

    this.drupalRESTService.httpGET(`/api_rest/v1/taxonomy_term/${packageTermId}`).subscribe(
      (data: any) => {
        if (data?.name) {
          // Store the data in packageData for consistency
          this.packageData = { name: data.name } as PackageData;
        }
        this.loading = false;
      },
      (error) => {
        LoggerService.error('Error fetching package name from taxonomy:', error);
        this.loading = false;
      }
    );
  }

  // Add the missing methods for handling related data
  fetchStudentData(studentId: string | number) {
    this.entityRESTService.getEntity('student_accounts', 'student_account', studentId).subscribe(
      (data) => {
        this.studentData = data || {};
        this.loading = false;
      },
      (error) => {
        LoggerService.error('Error fetching student data:', error);
        this.loading = false;
      }
    );
  }

  private isPackageDataLoading = false;
  
  fetchPackageData(packageId: string | number) {
    // Prevent duplicate fetches
    if (this.isPackageDataLoading) {
      LoggerService.log('Package data fetch already in progress, skipping duplicate request');
      return;
    }
    
    // Check if we already have the data
    if (this.packageData?.name || this.packageData?.field_package_name) {
      LoggerService.log('Package data already exists, skipping fetch');
      return;
    }
    
    this.isPackageDataLoading = true;
    
    this.entityRESTService.getEntity('packages', 'packages', packageId).subscribe(
      (data) => {
        this.packageData = data || {};
        this.isPackageDataLoading = false;

        // If we have no name but have field_enrollment_package_name, try to get its value
        if ((!this.packageData['name'] || this.packageData['name'] === 'N/A') &&
            this.packageData['field_enrollment_package_name']) {
          // Check if it's just an ID that needs to be resolved
          if (typeof this.packageData['field_enrollment_package_name'] === 'string' &&
              !isNaN(Number(this.packageData['field_enrollment_package_name']))) {
            // Try to get the package name from taxonomy
            this.drupalRESTService.httpGET(`/api_rest/v1/taxonomy_term/${this.packageData['field_enrollment_package_name']}`)
              .subscribe(
                (taxData: any) => {
                  if (taxData?.name) {
                    this.packageData['name'] = taxData.name;
                  }
                },
                (error) => {
                  LoggerService.error('Error fetching package taxonomy term:', error);
                }
              );
          } else {
            // If it's already a name, use it directly
            this.packageData['name'] = this.packageData['field_enrollment_package_name'];
          }
        }

        this.loading = false;
      },
      (error) => {
        LoggerService.error('Error fetching package data:', error);
        this.isPackageDataLoading = false;
        this.loading = false;
      }
    );
  }

  // Add a new method to get package data by ID
  fetchPackageDataById(packageId: string | number | PackageReference) {
    // Only proceed if packageId is a string or number
    if (!packageId || typeof packageId === 'object') return;

    // Use the EntityRESTService to get the package data
    this.entityRESTService.getEntity('packages', 'package', packageId)
      .subscribe({
        next: (packageData: any) => {
          if (packageData) {
            LoggerService.log('Fetched package data:', packageData);
            if (packageData.field_package_name) {
              this.packageData = {
                name: packageData.field_package_name,
                id: packageId
              };
            } else if (packageData.title) {
              this.packageData = {
                name: packageData.title,
                id: packageId
              };
            }
          }
        },
        error: (err) => {
          LoggerService.error('Error fetching package by ID:', err);
        }
      });
  }

  // Add new method for loading package data
  loadPackageById(packageId: string): void {
    if (!packageId || this.isPackageDataLoading) return;

    // Check if we already have resolved package data
    if (this.packageData?.name || this.packageData?.field_package_name) return;

    this.isPackageDataLoading = true;
    this.entityRESTService.getEntity('packages', 'package', packageId)
      .subscribe({
        next: (data: any) => {
          this.isPackageDataLoading = false;
          if (data) {
            if (data.field_package_name) {
              this.packageData = { name: data.field_package_name, id: packageId };
            } else if (data.title) {
              this.packageData = { name: data.title, id: packageId };
            } else {
              this.packageData = data;
            }
          }
        },
        error: (err) => {
          this.isPackageDataLoading = false;
          LoggerService.error('Error loading package data:', err);
        }
      });
  }
}
